import { useMemo, useState } from 'react';
import { Modal } from '../../components/ui/Modal';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Alert } from '../../components/ui/Alert';
import { LoadingState } from '../../components/ui/States';
import { useTeams, useCreateTeam, useUpdateTeam, useDeleteTeam } from '../../hooks/useTeams';
import { useUsers } from '../../hooks/useUsers';
import type { Team } from '../../api/teams';
import { orderedFeatures } from './AccessModals';

type EditTarget = Team | 'new' | null;

/** Manage teams: each team is a named bundle of feature grants assignable to people. */
export function TeamsModal({ onClose }: { onClose: () => void }) {
  const { data, isLoading } = useTeams();
  const deleteTeam = useDeleteTeam();
  const [editing, setEditing] = useState<EditTarget>(null);

  const teams = data?.teams ?? [];

  return (
    <Modal isOpen onClose={onClose} title="Teams" size="full">
      {isLoading ? (
        <LoadingState />
      ) : editing ? (
        <TeamEditor
          team={editing === 'new' ? null : editing}
          onDone={() => setEditing(null)}
        />
      ) : (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-body-sm text-text-tertiary">
              Teams grant access in bundles. Add members to a team here, or assign teams from each
              user's Access editor.
            </p>
            <Button variant="primary" size="sm" onClick={() => setEditing('new')}>
              New Team
            </Button>
          </div>

          {teams.length === 0 ? (
            <p className="text-body-sm text-text-tertiary text-center py-8">No teams yet.</p>
          ) : (
            <div className="divide-y divide-border-light">
              {teams.map((team) => (
                <div key={team.id} className="flex items-center justify-between py-3">
                  <div>
                    <p className="text-body-sm font-medium text-text-primary">{team.name}</p>
                    <p className="text-body-xs text-text-tertiary">
                      {team.memberCount} member{team.memberCount === 1 ? '' : 's'} •{' '}
                      {team.features.length} area{team.features.length === 1 ? '' : 's'}
                      {team.description ? ` • ${team.description}` : ''}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="ghost" size="sm" onClick={() => setEditing(team)}>
                      Edit
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={deleteTeam.isPending}
                      onClick={() => {
                        if (confirm(`Delete team "${team.name}"? Members lose its access.`)) {
                          deleteTeam.mutate(team.id);
                        }
                      }}
                    >
                      Delete
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="flex justify-end pt-4 border-t border-border-light">
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}

function TeamEditor({ team, onDone }: { team: Team | null; onDone: () => void }) {
  const create = useCreateTeam();
  const update = useUpdateTeam();
  const rows = useMemo(orderedFeatures, []);
  const { data: usersData, isLoading: usersLoading } = useUsers({ pageSize: 100 });
  const allUsers = usersData?.users ?? [];

  const [name, setName] = useState(team?.name ?? '');
  const [description, setDescription] = useState(team?.description ?? '');
  const [features, setFeatures] = useState<Set<string>>(new Set(team?.features ?? []));
  const [members, setMembers] = useState<Set<string>>(new Set(team?.memberIds ?? []));

  const isPending = create.isPending || update.isPending;

  const toggle = (id: string) => {
    setFeatures((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleMember = (id: string) => {
    setMembers((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSave = () => {
    // Settings is always available, so never persist it as a team grant.
    const payload = {
      name: name.trim(),
      description: description.trim() || null,
      features: rows.filter((f) => !f.alwaysAllowed && features.has(f.id)).map((f) => f.id),
      memberIds: allUsers.filter((u) => members.has(u.id)).map((u) => u.id),
    };
    if (team) {
      update.mutate({ id: team.id, data: payload }, { onSuccess: onDone });
    } else {
      create.mutate(payload, { onSuccess: onDone });
    }
  };

  const error = create.error || update.error;

  return (
    <div className="space-y-4">
      <h3 className="text-body-md font-medium text-text-primary">
        {team ? `Edit ${team.name}` : 'New Team'}
      </h3>

      {error && <Alert variant="error">{(error as Error).message}</Alert>}

      <Input
        label="Name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="e.g. CS Team"
        required
      />
      <Input
        label="Description"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        placeholder="Optional"
      />

      <div>
        <p className="text-body-sm font-medium text-text-primary mb-2">Granted access</p>
        <div className="max-h-[45vh] overflow-y-auto divide-y divide-border-light border border-border-light rounded-md">
          {rows.map((f) => (
            <label
              key={f.id}
              className={`flex items-center gap-3 py-2.5 px-3 cursor-pointer ${
                f.parentId ? 'pl-8' : ''
              } ${f.alwaysAllowed ? 'opacity-50' : ''}`}
            >
              <input
                type="checkbox"
                checked={f.alwaysAllowed || features.has(f.id)}
                disabled={f.alwaysAllowed}
                onChange={() => toggle(f.id)}
                className="h-4 w-4 rounded border-border accent-text-primary disabled:opacity-60"
              />
              <span className="text-body-sm text-text-primary">{f.label}</span>
              {f.alwaysAllowed && (
                <span className="text-body-xs text-text-tertiary">(always available)</span>
              )}
            </label>
          ))}
        </div>
      </div>

      <div>
        <p className="text-body-sm font-medium text-text-primary mb-2">
          Members{members.size > 0 ? ` (${members.size})` : ''}
        </p>
        {usersLoading ? (
          <p className="text-body-xs text-text-tertiary">Loading people…</p>
        ) : allUsers.length === 0 ? (
          <p className="text-body-xs text-text-tertiary">No people to add yet.</p>
        ) : (
          <div className="max-h-[30vh] overflow-y-auto divide-y divide-border-light border border-border-light rounded-md">
            {allUsers.map((u) => (
              <label
                key={u.id}
                className="flex items-center gap-3 py-2.5 px-3 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={members.has(u.id)}
                  onChange={() => toggleMember(u.id)}
                  className="h-4 w-4 rounded border-border accent-text-primary"
                />
                <span className="text-body-sm text-text-primary">{u.name}</span>
                <span className="text-body-xs text-text-tertiary">{u.email}</span>
                {u.role === 'admin' && (
                  <span className="text-body-xs text-text-tertiary">(admin — full access)</span>
                )}
              </label>
            ))}
          </div>
        )}
      </div>

      <div className="flex justify-end gap-3 pt-4 border-t border-border-light">
        <Button variant="outline" onClick={onDone}>
          Cancel
        </Button>
        <Button variant="primary" disabled={isPending || !name.trim()} onClick={handleSave}>
          {isPending ? 'Saving...' : team ? 'Save Team' : 'Create Team'}
        </Button>
      </div>
    </div>
  );
}
